import React, { useState, useEffect } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Badge, 
  Button, 
  Modal, 
  Form, 
  InputGroup, 
  Alert, 
  Table,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import axiosInstance from '../../helpers/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faSearch, 
  faFilter, 
  faPlus, 
  faDownload, 
  faSort, 
  faSortUp, 
  faSortDown,
  faBuilding,
  faUserGraduate,
  faUserTie,
  faCrown,
  faIdCard,
  faEnvelope,
  faPhone,
  faDollarSign,
  faCalendarAlt,
  faEye,
  faChartLine,
  faEdit,
  faShield,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faPercentage,
  faFileInvoiceDollar,
  faHandshake,
  faStar,
  faUserXmark,
  faExclamationTriangle,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import BrokerApiService from '../../services/brokerApiService';
import AddAgentModal from './AddAgentModal';
import EditAgentModal from './EditAgentModal';

interface Agent {
  id: string;
  name: string;
  email: string;
  phone: string;
  licenseNumber: string;
  licenseType: 'BR' | 'SA';
  plan: 'essential' | 'professional' | 'deal_ai' | 'agent_kairos' | 'premium';
  status: 'active' | 'inactive';
  monthlyFee: number;
  joinDate: string;
  lastActive: string;
  localType: string;
  // Authentication tracking fields
  memberStatus?: 'pending' | 'invited' | 'active' | 'inactive';
  isAuthenticated?: boolean;
  userId?: string;
  invitationSent?: boolean;
  authenticationDate?: string;
  // Compensation tracking fields
  eoFee?: number;
  commissionSplitAgent?: number;
  commissionSplitBroker?: number;
  transactionFee?: number;
  hasPartnerSplits?: boolean;
  ytdGci?: number;
  totalTransactions?: number;
  // Admin tracking
  isAdmin?: boolean;
  memberRole?: string;
  // Effective date tracking
  effectiveDate?: string;
  terminationDate?: string;
  terminationReason?: string;
  isActive?: boolean;
}

interface AgentDirectoryProps {
  brokerageId?: string;
  totalAgents?: number;
}

const AgentDirectory: React.FC<AgentDirectoryProps> = ({ 
  brokerageId, 
  totalAgents 
}) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [filteredAgents, setFilteredAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [licenseFilter, setLicenseFilter] = useState('all');
  const [sortField, setSortField] = useState<keyof Agent>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<any[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  
  // Admin confirmation modal state
  const [showAdminConfirmModal, setShowAdminConfirmModal] = useState(false);
  const [adminConfirmAction, setAdminConfirmAction] = useState<{ agent: Agent, isGranting: boolean } | null>(null);
  const [isUpdatingAdmin, setIsUpdatingAdmin] = useState(false);
  
  // Invitation confirmation modal state
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [agentToInvite, setAgentToInvite] = useState<Agent | null>(null);
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);

  // Transform API response to component format
  const transformAgentData = (apiAgents: any[]): Agent[] => {
    return apiAgents.map(agent => ({
      id: agent.id,
      name: agent.agent_full_name,
      email: agent.agent_email,
      phone: agent.agent_phone,
      licenseNumber: agent.agent_license_number,
      licenseType: agent.license_type,
      plan: agent.individual_plan,
      status: agent.is_active ? 'active' : 'inactive',
      monthlyFee: agent.monthly_fee,
      joinDate: agent.created_at ? new Date(agent.created_at).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      }) : 'Jan 1, 2023',
      lastActive: agent.last_activity ? new Date(agent.last_activity).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }) : 'Unknown',
      localType: agent.local_type || 'Realtor',
      // Authentication tracking data from brokerage_team_members
      memberStatus: agent.member_status || 'pending',
      isAuthenticated: agent.user_id ? true : false,
      userId: agent.user_id,
      invitationSent: agent.member_status === 'invited' || agent.member_status === 'active',
      authenticationDate: agent.user_id ? agent.joined_at || agent.updated_at : undefined,
      // Commission tracking fields
      eoFee: agent.eo_fee || 0,
      commissionSplitAgent: agent.commission_split_agent || 80,
      commissionSplitBroker: agent.commission_split_broker || 20,
      transactionFee: agent.transaction_fee || 0,
      hasPartnerSplits: agent.has_partner_splits || false,
      ytdGci: agent.ytd_gci || 0,
      totalTransactions: agent.total_transactions || 0,
      // Admin tracking
      isAdmin: agent.is_admin || false,
      memberRole: agent.member_role || 'agent',
      // Effective date tracking
      effectiveDate: agent.effective_date,
      terminationDate: agent.termination_date,
      terminationReason: agent.termination_reason,
      isActive: agent.is_active !== false
    }));
  };

  // Fetch agents from API
  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Enhanced debug logging
      console.log('🎯 [AGENT DIRECTORY] Starting fetchAgents...');
      console.log('📍 [AGENT DIRECTORY] brokerageId prop:', brokerageId);
      console.log('📍 [AGENT DIRECTORY] localStorage brokerageId:', localStorage.getItem('brokerageId'));
      console.log('📍 [AGENT DIRECTORY] localStorage brokerageName:', localStorage.getItem('brokerageName'));
      
      if (!brokerageId) {
        console.error('❌ [AGENT DIRECTORY] No brokerage ID provided');
        console.log('Debug info:', {
          brokerageIdProp: brokerageId,
          localStorageBrokerageId: localStorage.getItem('brokerageId'),
          localStorageBrokerageName: localStorage.getItem('brokerageName')
        });
        setError('No brokerage selected - please ensure you are associated with a brokerage');
        setLoading(false);
        return;
      }
      
      console.log('🔍 [FRONTEND] Fetching agents for brokerage:', brokerageId);
      console.log('📊 [FRONTEND] Auth token exists:', !!localStorage.getItem('auth_token'));
      console.log('🌐 [FRONTEND] About to call BrokerApiService.getAgents...');
      
      const apiAgents = await BrokerApiService.getAgents(brokerageId);
      console.log('📊 [FRONTEND] Received agents data:', { count: apiAgents?.length || 0, agents: apiAgents });
      
      if (apiAgents && apiAgents.length > 0) {
        const transformedAgents = transformAgentData(apiAgents);
        setAgents(transformedAgents);
        setFilteredAgents(transformedAgents);
      } else {
        setError('No agents found for this brokerage');
        setAgents([]);
        setFilteredAgents([]);
      }
    } catch (err: any) {
      console.error('❌ [AGENT DIRECTORY] Error fetching agents:', err);
      console.error('❌ [AGENT DIRECTORY] Error message:', err.message);
      console.error('❌ [AGENT DIRECTORY] Error stack:', err.stack);
      
      // More specific error messages
      if (err.message.includes('timeout')) {
        setError('Request timeout - server is taking too long to respond');
      } else if (err.message.includes('401') || err.message.includes('403')) {
        setError('Authentication error - please log in again');
      } else if (err.message.includes('404')) {
        setError('Brokerage not found - please check your settings');
      } else {
        setError('Unable to load agents - please refresh the page or try again later');
      }
      setAgents([]);
      setFilteredAgents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, [brokerageId]); // fetchAgents is stable, no need to include it

  // Filter and search logic
  useEffect(() => {
    let filtered = agents.filter(agent => {
      const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           agent.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           agent.licenseNumber.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesPlan = planFilter === 'all' || agent.plan === planFilter;
      const matchesStatus = statusFilter === 'all' || agent.status === statusFilter;
      const matchesLicense = licenseFilter === 'all' || agent.licenseType === licenseFilter;

      return matchesSearch && matchesPlan && matchesStatus && matchesLicense;
    });

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });

    setFilteredAgents(filtered);
  }, [agents, searchTerm, planFilter, statusFilter, licenseFilter, sortField, sortDirection]);

  const handleSort = (field: keyof Agent) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: keyof Agent) => {
    if (sortField !== field) return faSort;
    return sortDirection === 'asc' ? faSortUp : faSortDown;
  };

  const getPlanBadge = (plan: string) => {
    const badges = {
      essential: { bg: 'secondary', icon: faBuilding },
      professional: { bg: 'primary', icon: faUserGraduate },
      deal_ai: { bg: 'info', icon: faHandshake },
      agent_kairos: { bg: 'success', icon: faCrown },
      premium: { bg: 'warning', icon: faStar } // Legacy support
    };
    
    const badge = badges[plan as keyof typeof badges] || { bg: 'secondary', icon: faBuilding };
    const displayName = plan === 'deal_ai' ? 'Deal AI' : plan === 'agent_kairos' ? 'Agent Kairos' : plan.charAt(0).toUpperCase() + plan.slice(1);
    
    return (
      <Badge bg={badge.bg} className="d-flex align-items-center">
        <FontAwesomeIcon icon={badge.icon} className="me-1" size="xs" />
        {displayName}
      </Badge>
    );
  };

  const getAgentStatusBadge = (agent: Agent) => {
    // Check effective dates
    const today = new Date();
    const effectiveDate = agent.effectiveDate ? new Date(agent.effectiveDate) : null;
    const terminationDate = agent.terminationDate ? new Date(agent.terminationDate) : null;
    
    // Determine status
    if (terminationDate && terminationDate <= today) {
      return (
        <Badge bg="danger" className="ms-2">
          <FontAwesomeIcon icon={faUserXmark} className="me-1" size="xs" />
          Terminated
        </Badge>
      );
    }
    
    if (effectiveDate && effectiveDate > today) {
      return (
        <Badge bg="warning" className="ms-2">
          <FontAwesomeIcon icon={faClock} className="me-1" size="xs" />
          Pending Start
        </Badge>
      );
    }
    
    if (terminationDate && terminationDate > today) {
      const daysUntilTermination = Math.ceil((terminationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return (
        <Badge bg="warning" className="ms-2">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" size="xs" />
          Leaving in {daysUntilTermination} days
        </Badge>
      );
    }
    
    // Calculate tenure if active
    if (effectiveDate) {
      const tenureMs = today.getTime() - effectiveDate.getTime();
      const tenureDays = Math.floor(tenureMs / (1000 * 60 * 60 * 24));
      const years = Math.floor(tenureDays / 365);
      const months = Math.floor((tenureDays % 365) / 30);
      
      if (years > 0) {
        return (
          <Badge bg="success" className="ms-2">
            <FontAwesomeIcon icon={faStar} className="me-1" size="xs" />
            {years}y {months}m
          </Badge>
        );
      }
    }
    
    return null;
  };

  const getAuthenticationBadge = (agent: Agent) => {
    if (agent.isAuthenticated && agent.userId) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              ✅ Authenticated User<br/>
              Registered: {agent.authenticationDate ? new Date(agent.authenticationDate).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric'
              }) : 'Recently'}<br/>
              User ID: {agent.userId}
            </Tooltip>
          }
        >
          <Badge bg="success" className="d-flex align-items-center ms-1">
            <FontAwesomeIcon icon={faShield} className="me-1" size="xs" />
            <FontAwesomeIcon icon={faCheckCircle} size="xs" />
          </Badge>
        </OverlayTrigger>
      );
    }
    
    if (agent.invitationSent) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              📧 Invitation Sent<br/>
              Status: {agent.memberStatus}<br/>
              Waiting for registration...
            </Tooltip>
          }
        >
          <Badge bg="warning" className="d-flex align-items-center ms-1">
            <FontAwesomeIcon icon={faClock} className="me-1" size="xs" />
            Invited
          </Badge>
        </OverlayTrigger>
      );
    }
    
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip>
            ⏳ Pending<br/>
            Status: {agent.memberStatus || 'pending'}<br/>
            No invitation sent yet
          </Tooltip>
        }
      >
        <Badge bg="secondary" className="d-flex align-items-center ms-1">
          <FontAwesomeIcon icon={faTimesCircle} className="me-1" size="xs" />
          Pending
        </Badge>
      </OverlayTrigger>
    );
  };

  const getMemberStatusBadge = (memberStatus?: string) => {
    const statusConfig = {
      pending: { bg: 'secondary', icon: faClock, text: 'Pending' },
      invited: { bg: 'warning', icon: faEnvelope, text: 'Invited' },
      active: { bg: 'success', icon: faCheckCircle, text: 'Active' },
      inactive: { bg: 'danger', icon: faTimesCircle, text: 'Inactive' }
    };
    
    const config = statusConfig[memberStatus as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <Badge bg={config.bg} className="d-flex align-items-center">
        <FontAwesomeIcon icon={config.icon} className="me-1" size="xs" />
        {config.text}
      </Badge>
    );
  };

  const handleAddAgent = () => {
    setShowAddModal(true);
  };

  // CSV Export Function
  const handleExportCSV = () => {
    try {
      // Define CSV headers
      const headers = [
        'ID',
        'Name',
        'Email',
        'Phone',
        'License Number',
        'License Type',
        'Plan',
        'Status',
        'Is Admin',
        'Monthly Fee',
        'Join Date',
        'Last Active',
        'Local Type',
        'Member Status',
        'Is Authenticated',
        'User ID',
        'E&O Fee',
        'Commission Split Agent',
        'Commission Split Broker',
        'Transaction Fee',
        'Has Partner Splits',
        'YTD GCI',
        'Total Transactions'
      ];

      // Convert agents to CSV rows
      const csvRows = [
        headers.join(','), // Header row
        ...filteredAgents.map(agent => [
          agent.id,
          `"${agent.name}"`, // Quote to handle commas in names
          agent.email,
          agent.phone,
          agent.licenseNumber,
          agent.licenseType,
          agent.plan,
          agent.status,
          agent.isAdmin ? 'true' : 'false',
          agent.monthlyFee,
          `"${agent.joinDate}"`,
          `"${agent.lastActive}"`,
          `"${agent.localType}"`,
          agent.memberStatus || 'pending',
          agent.isAuthenticated ? 'true' : 'false',
          agent.userId || '',
          agent.eoFee || 0,
          agent.commissionSplitAgent || 80,
          agent.commissionSplitBroker || 20,
          agent.transactionFee || 0,
          agent.hasPartnerSplits ? 'true' : 'false',
          agent.ytdGci || 0,
          agent.totalTransactions || 0
        ].join(','))
      ];

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `agents_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log('✅ CSV exported successfully');
    } catch (error) {
      console.error('❌ Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  // Parse CSV file for import
  const parseCSV = (text: string): any[] => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g)?.map(v => v.trim().replace(/^"|"$/g, '')) || [];

      if (values.length === headers.length) {
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index];
        });
        data.push(row);
      }
    }

    return data;
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportFile(file);
    const reader = new FileReader();

    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsedData = parseCSV(text);
      setImportPreview(parsedData);
    };

    reader.readAsText(file);
  };

  // Import and update agents from CSV
  const handleImportCSV = async () => {
    if (!importPreview.length) {
      alert('No data to import');
      return;
    }

    try {
      setImportLoading(true);

      // Transform CSV data to agent updates
      const updates = importPreview.map(row => ({
        id: row.ID,
        name: row.Name,
        email: row.Email,
        phone: row.Phone,
        licenseNumber: row['License Number'],
        licenseType: row['License Type'],
        plan: row.Plan,
        status: row.Status === 'active',
        monthlyFee: parseFloat(row['Monthly Fee']),
        localType: row['Local Type'],
        eoFee: parseFloat(row['E&O Fee'] || '0'),
        commissionSplitAgent: parseFloat(row['Commission Split Agent'] || '80'),
        commissionSplitBroker: parseFloat(row['Commission Split Broker'] || '20'),
        transactionFee: parseFloat(row['Transaction Fee'] || '0'),
        hasPartnerSplits: row['Has Partner Splits'] === 'true'
      }));

      console.log('🔄 Importing agents:', updates);

      // Call API to bulk update agents
      const response = await fetch(`${process.env.NODE_ENV === 'production' ? '/api/broker' : 'http://localhost:8080/api/broker'}/agents/bulk-update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          brokerageId: brokerageId || localStorage.getItem('brokerageId'),
          agents: updates
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to import agents: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        alert(`✅ Successfully imported ${updates.length} agents!`);
        setShowImportModal(false);
        setImportFile(null);
        setImportPreview([]);
        // Refresh the agents list
        await fetchAgents();
      } else {
        throw new Error(result.error || 'Failed to import agents');
      }
    } catch (error: any) {
      console.error('❌ Error importing CSV:', error);
      alert(`Failed to import agents: ${error.message}`);
    } finally {
      setImportLoading(false);
    }
  };

  const handleSendInvitation = async (agent: Agent) => {
    // Show custom modal instead of browser confirm
    setAgentToInvite(agent);
    setShowInvitationModal(true);
  };
  
  const confirmSendInvitation = async () => {
    if (!agentToInvite) return;
    
    try {
      setIsSendingInvitation(true);
      console.log('📧 Sending invitation to:', agentToInvite.name);
      
      // Call the API to send invitation using axiosInstance
      // Using the main broker endpoint, not the invitations sub-path
      const response = await axiosInstance.post('/broker/send-invitation', {
        memberId: agentToInvite.id,
        brokerageId: brokerageId || localStorage.getItem('brokerageId'),
        agentData: {
          agent_email: agentToInvite.email,
          // Remove "(Pending Invitation)" suffix before splitting name
          agent_first_name: agentToInvite.name.replace(' (Pending Invitation)', '').split(' ')[0],
          agent_last_name: agentToInvite.name.replace(' (Pending Invitation)', '').split(' ').slice(1).join(' ') || '',
          agent_license_number: agentToInvite.licenseNumber,
          subscription_plan: agentToInvite.plan,
          monthly_fee: agentToInvite.monthlyFee
        },
        brokerageData: {
          brokerage_name: localStorage.getItem('brokerageName') || 'Your Brokerage'
        }
      });

      // With axios, the response data is in response.data
      if (response.data.success) {
        // Close modal and show success
        setShowInvitationModal(false);
        setAgentToInvite(null);
        
        // Show success alert (you could also use a toast notification)
        alert(`✅ Invitation successfully sent to ${agentToInvite.name}!`);
        
        // Refresh the agents list to update the status
        await fetchAgents();
      } else {
        throw new Error(response.data.error || 'Failed to send invitation');
      }
    } catch (error: any) {
      console.error('❌ Error sending invitation:', error);
      alert(`Failed to send invitation: ${error.message}`);
    } finally {
      setIsSendingInvitation(false);
    }
  };


  const AgentModal: React.FC = () => (
    <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
      <Modal.Header 
        closeButton
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Modal.Title className="d-flex align-items-center">
          <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
          Agent Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedAgent && (
          <>
            <Row className="mb-3">
              <Col md={6}>
                <Card className="h-100 border-0" style={{ background: 'rgba(0, 212, 255, 0.05)' }}>
                  <Card.Body>
                    <h6 className="fw-bold mb-3">Contact Information</h6>
                    <div className="mb-2">
                      <FontAwesomeIcon icon={faIdCard} className="me-2 text-muted" />
                      <strong>Name:</strong> {selectedAgent.name}
                      {selectedAgent.isAdmin && (
                        <Badge 
                          bg="warning" 
                          className="ms-2"
                          style={{ fontSize: '0.7rem' }}
                        >
                          <FontAwesomeIcon icon={faCrown} size="xs" className="me-1" />
                          Admin
                        </Badge>
                      )}
                    </div>
                    <div className="mb-2">
                      <FontAwesomeIcon icon={faEnvelope} className="me-2 text-muted" />
                      <strong>Email:</strong> {selectedAgent.email}
                    </div>
                    <div className="mb-2">
                      <FontAwesomeIcon icon={faPhone} className="me-2 text-muted" />
                      <strong>Phone:</strong> {selectedAgent.phone}
                    </div>
                    <div className="mb-2">
                      <FontAwesomeIcon icon={faIdCard} className="me-2 text-muted" />
                      <strong>License:</strong> {selectedAgent.licenseNumber}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="h-100 border-0" style={{ background: 'rgba(16, 185, 129, 0.05)' }}>
                  <Card.Body>
                    <h6 className="fw-bold mb-3">Plan Information</h6>
                    <div className="mb-2">
                      <strong>Current Plan:</strong> {getPlanBadge(selectedAgent.plan)}
                    </div>
                    <div className="mb-2">
                      <strong>Monthly Fee:</strong> 
                      <span className="text-success fw-bold ms-1">
                        ${selectedAgent.monthlyFee}
                      </span>
                    </div>
                    <div className="mb-2">
                      <strong>Join Date:</strong> {selectedAgent.joinDate}
                    </div>
                    <div className="mb-2">
                      <strong>Last Active:</strong> {selectedAgent.lastActive}
                    </div>
                    <div className="mb-2">
                      <strong>Type:</strong> {selectedAgent.localType}
                    </div>
                    <div className="mb-2">
                      <strong>Member Status:</strong> {getMemberStatusBadge(selectedAgent.memberStatus)}
                    </div>
                    {selectedAgent.isAuthenticated && (
                      <div className="mb-2">
                        <FontAwesomeIcon icon={faShield} className="me-2 text-success" />
                        <strong>Authentication:</strong> 
                        <Badge bg="success" className="ms-2">
                          <FontAwesomeIcon icon={faCheckCircle} className="me-1" size="xs" />
                          Verified User
                        </Badge>
                      </div>
                    )}
                    {selectedAgent.authenticationDate && (
                      <div className="mb-2">
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-muted" />
                        <strong>Registered:</strong> {new Date(selectedAgent.authenticationDate).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric'
                        })}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col>
                <Card className="border-0" style={{ background: 'rgba(251, 146, 60, 0.05)' }}>
                  <Card.Body>
                    <h6 className="fw-bold mb-3">Compensation & Fees</h6>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <FontAwesomeIcon icon={faPercentage} className="me-2 text-primary" />
                          <strong>Commission Split:</strong>
                          <div className="mt-1">
                            <Badge bg="info" className="me-2">Agent: {selectedAgent.commissionSplitAgent || 80}%</Badge>
                            <Badge bg="secondary">Broker: {selectedAgent.commissionSplitBroker || 20}%</Badge>
                          </div>
                        </div>
                        <div className="mb-3">
                          <FontAwesomeIcon icon={faShield} className="me-2 text-warning" />
                          <strong>E&O Fee:</strong> 
                          <span className="ms-1">${selectedAgent.eoFee || 0}</span>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2 text-success" />
                          <strong>Transaction Fee:</strong> 
                          <span className="ms-1">${selectedAgent.transactionFee || 0}</span>
                        </div>
                        <div className="mb-3">
                          <FontAwesomeIcon icon={faHandshake} className="me-2 text-info" />
                          <strong>Partner Splits:</strong> 
                          <span className="ms-1">
                            {selectedAgent.hasPartnerSplits ? (
                              <Badge bg="warning">Active</Badge>
                            ) : (
                              <Badge bg="secondary">None</Badge>
                            )}
                          </span>
                        </div>
                      </Col>
                    </Row>
                    <hr />
                    <Row>
                      <Col md={6}>
                        <div className="mb-2">
                          <FontAwesomeIcon icon={faDollarSign} className="me-2 text-success" />
                          <strong>YTD GCI:</strong> 
                          <span className="ms-1 fw-bold text-success">
                            ${(selectedAgent.ytdGci || 0).toLocaleString()}
                          </span>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-2">
                          <FontAwesomeIcon icon={faChartLine} className="me-2 text-primary" />
                          <strong>Total Transactions:</strong> 
                          <span className="ms-1">{selectedAgent.totalTransactions || 0}</span>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            {/* Admin Privileges Section */}
            <Row className="mt-3">
              <Col>
                <Card className="border-0" style={{ background: 'rgba(239, 68, 68, 0.05)' }}>
                  <Card.Body>
                    <h6 className="fw-bold mb-3">
                      <FontAwesomeIcon icon={faCrown} className="me-2 text-warning" />
                      Admin Privileges
                    </h6>
                    <Form>
                      <Form.Check
                        type="switch"
                        id="admin-switch"
                        label={
                          <span>
                            <strong>Grant Admin Access</strong>
                            <br />
                            <small className="text-muted">
                              Admins can manage team members, view billing information, and access all broker features
                            </small>
                          </span>
                        }
                        checked={selectedAgent.isAdmin || false}
                        onChange={(e) => {
                          setAdminConfirmAction({ 
                            agent: selectedAgent, 
                            isGranting: e.target.checked 
                          });
                          setShowAdminConfirmModal(true);
                        }}
                      />
                    </Form>
                    {selectedAgent.isAdmin && (
                      <Alert variant="warning" className="mt-3 mb-0">
                        <FontAwesomeIcon icon={faCrown} className="me-2" />
                        <strong>Current Admin</strong> - This user has full administrative access to your brokerage
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowModal(false)}>
          Close
        </Button>
        <Button 
          variant="primary"
          onClick={() => {
            setShowModal(false);
            setShowEditModal(true);
          }}
        >
          <FontAwesomeIcon icon={faEdit} className="me-1" />
          Edit Agent
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <div className="agent-directory">
      {/* Header Section */}
      <Card 
        className="border-0 mb-4 ai-enhanced-card"
      >
        <Card.Header 
          className="border-bottom-0 py-4"
          style={{ 
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px 16px 0 0'
          }}
        >
          <Row className="align-items-center">
            <Col>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon 
                  icon={faUsers} 
                  className="me-3"
                  style={{ 
                    color: '#00d4ff', 
                    fontSize: '24px',
                    filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.5))'
                  }}
                />
                <div>
                  <h4 className="mb-1 fw-bold" style={{ color: '#1f2937' }}>
                    Agent Directory
                  </h4>
                  <p className="mb-0 text-muted">
                    Manage and monitor your {filteredAgents.length} agents
                  </p>
                </div>
              </div>
            </Col>
            <Col xs="auto">
              <div className="d-flex gap-2">
                <Button variant="outline-success" size="sm" onClick={() => setShowImportModal(true)}>
                  <FontAwesomeIcon icon={faDownload} className="me-1" style={{ transform: 'rotate(180deg)' }} />
                  Import
                </Button>
                <Button variant="outline-primary" size="sm" onClick={handleExportCSV}>
                  <FontAwesomeIcon icon={faDownload} className="me-1" />
                  Export
                </Button>
                <Button variant="primary" size="sm" onClick={handleAddAgent}>
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Add Agent
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body>
          {/* Filters and Search */}
          <Row className="g-3 mb-4">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search agents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Select 
                value={planFilter} 
                onChange={(e) => setPlanFilter(e.target.value)}
              >
                <option value="all">All Plans</option>
                <option value="essential">Essential</option>
                <option value="professional">Professional</option>
                <option value="deal_ai">Deal AI</option>
                <option value="agent_kairos">Agent Kairos</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select 
                value={licenseFilter} 
                onChange={(e) => setLicenseFilter(e.target.value)}
              >
                <option value="all">All Licenses</option>
                <option value="BR">Broker (BR)</option>
                <option value="SA">Agent (SA)</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button variant="outline-secondary" className="w-100">
                <FontAwesomeIcon icon={faFilter} className="me-1" />
                Advanced
              </Button>
            </Col>
          </Row>

          {/* Agents Table */}
          {loading ? (
            <div className="ai-loading-indicator">
              <div className="ai-loading-spinner me-3"></div>
              <span className="text-muted">Loading agents...</span>
            </div>
          ) : error ? (
            <Alert variant="danger" className="text-center">
              {error}
            </Alert>
          ) : (
            <>
              {/* Scroll hint for smaller screens */}
              <div className="d-lg-none text-muted small mb-2 text-center">
                <FontAwesomeIcon icon={faChartLine} className="me-1" />
                Swipe horizontally to see all columns
              </div>
              <div className="table-responsive" style={{ overflowX: 'auto', maxWidth: '100%' }}>
                <Table hover className="mb-0" style={{ minWidth: '1200px' }}>
                <thead style={{ background: 'rgba(0, 212, 255, 0.05)' }}>
                  <tr>
                    <th 
                      className="border-0 py-3 cursor-pointer"
                      onClick={() => handleSort('name')}
                    >
                      <div className="d-flex align-items-center">
                        Agent Name
                        <FontAwesomeIcon 
                          icon={getSortIcon('name')} 
                          className="ms-1 text-muted" 
                          size="sm"
                        />
                      </div>
                    </th>
                    <th className="border-0 py-3">Contact</th>
                    <th 
                      className="border-0 py-3 cursor-pointer"
                      onClick={() => handleSort('licenseNumber')}
                    >
                      <div className="d-flex align-items-center">
                        License
                        <FontAwesomeIcon 
                          icon={getSortIcon('licenseNumber')} 
                          className="ms-1 text-muted" 
                          size="sm"
                        />
                      </div>
                    </th>
                    <th 
                      className="border-0 py-3 cursor-pointer"
                      onClick={() => handleSort('plan')}
                    >
                      <div className="d-flex align-items-center">
                        Plan
                        <FontAwesomeIcon 
                          icon={getSortIcon('plan')} 
                          className="ms-1 text-muted" 
                          size="sm"
                        />
                      </div>
                    </th>
                    <th 
                      className="border-0 py-3 cursor-pointer"
                      onClick={() => handleSort('monthlyFee')}
                    >
                      <div className="d-flex align-items-center">
                        Monthly Fee
                        <FontAwesomeIcon 
                          icon={getSortIcon('monthlyFee')} 
                          className="ms-1 text-muted" 
                          size="sm"
                        />
                      </div>
                    </th>
                    <th className="border-0 py-3">Commission Split</th>
                    <th className="border-0 py-3">E&O Fee</th>
                    <th className="border-0 py-3">Member Status</th>
                    <th className="border-0 py-3">Last Active</th>
                    <th className="border-0 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAgents.map((agent) => (
                    <tr key={agent.id} className="ai-transition">
                      <td className="py-3">
                        <div className="d-flex align-items-center">
                          <div 
                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{
                              width: '40px',
                              height: '40px',
                              background: agent.licenseType === 'BR' 
                                ? 'linear-gradient(135deg, #10b981, #059669)' 
                                : 'linear-gradient(135deg, #00d4ff, #0ea5e9)',
                              color: 'white'
                            }}
                          >
                            <FontAwesomeIcon 
                              icon={agent.licenseType === 'BR' ? faUserTie : faUsers} 
                              size="sm"
                            />
                          </div>
                          <div>
                            <div className="fw-semibold d-flex align-items-center">
                              {agent.name}
                              {agent.isAdmin && (
                                <Badge 
                                  bg="warning" 
                                  className="ms-2 d-inline-flex align-items-center"
                                  style={{ 
                                    fontSize: '0.75rem',
                                    padding: '0.25rem 0.5rem',
                                    fontWeight: '600'
                                  }}
                                >
                                  <FontAwesomeIcon icon={faCrown} size="xs" className="me-1" />
                                  Admin
                                </Badge>
                              )}
                              {getAgentStatusBadge(agent)}
                              {getAuthenticationBadge(agent)}
                            </div>
                            <small className="text-muted">{agent.localType}</small>
                          </div>
                        </div>
                      </td>
                      <td className="py-3">
                        <div>
                          <div className="mb-1">
                            <FontAwesomeIcon icon={faEnvelope} className="me-1 text-muted" size="xs" />
                            <small>{agent.email}</small>
                          </div>
                          <div>
                            <FontAwesomeIcon icon={faPhone} className="me-1 text-muted" size="xs" />
                            <small>{agent.phone}</small>
                          </div>
                        </div>
                      </td>
                      <td className="py-3">
                        <div>
                          <Badge 
                            bg={agent.licenseType === 'BR' ? 'success' : 'primary'}
                            className="mb-1"
                          >
                            {agent.licenseType}
                          </Badge>
                          <div>
                            <small className="text-muted">{agent.licenseNumber}</small>
                          </div>
                        </div>
                      </td>
                      <td className="py-3">
                        {getPlanBadge(agent.plan)}
                      </td>
                      <td className="py-3">
                        <span className="fw-bold text-success">
                          ${agent.monthlyFee}
                        </span>
                      </td>
                      <td className="py-3">
                        <Badge bg="info" className="d-flex align-items-center" style={{ width: 'fit-content' }}>
                          <FontAwesomeIcon icon={faPercentage} className="me-1" size="xs" />
                          {agent.commissionSplitAgent || 80}/{agent.commissionSplitBroker || 20}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <span className="text-muted">
                          ${agent.eoFee || 0}
                        </span>
                      </td>
                      <td className="py-3">
                        {getMemberStatusBadge(agent.memberStatus)}
                      </td>
                      <td className="py-3">
                        <small className="text-muted">{agent.lastActive}</small>
                      </td>
                      <td className="py-3 text-center">
                        <div className="d-flex justify-content-center gap-1">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>View Details</Tooltip>}
                          >
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => {
                                setSelectedAgent(agent);
                                setShowModal(true);
                              }}
                            >
                              <FontAwesomeIcon icon={faEye} size="xs" />
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>View Analytics</Tooltip>}
                          >
                            <Button variant="outline-success" size="sm">
                              <FontAwesomeIcon icon={faChartLine} size="xs" />
                            </Button>
                          </OverlayTrigger>
                          {/* Show invite button only for pending agents who haven't been invited */}
                          {agent.memberStatus === 'pending' && !agent.invitationSent && (
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>Send Invitation Email</Tooltip>}
                            >
                              <Button 
                                variant="outline-info" 
                                size="sm"
                                onClick={() => handleSendInvitation(agent)}
                              >
                                <FontAwesomeIcon icon={faEnvelope} size="xs" />
                              </Button>
                            </OverlayTrigger>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            </>
          )}
        </Card.Body>
      </Card>

      <AgentModal />

      {/* Add Agent Modal */}
      <AddAgentModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        brokerageId={brokerageId || localStorage.getItem('brokerageId') || ''}
        onAgentAdded={() => {
          setShowAddModal(false);
          // Refresh the agents list without page reload
          fetchAgents();
        }}
      />
      
      {/* Edit Agent Modal */}
      {selectedAgent && (
        <EditAgentModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          agent={selectedAgent}
          brokerageId={brokerageId || localStorage.getItem('brokerageId') || ''}
          onAgentUpdated={() => {
            setShowEditModal(false);
            // Refresh the agents list to show updated commission settings
            fetchAgents();
          }}
        />
      )}

      {/* Import CSV Modal */}
      <Modal
        show={showImportModal}
        onHide={() => {
          setShowImportModal(false);
          setImportFile(null);
          setImportPreview([]);
        }}
        size="lg"
      >
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Modal.Title className="d-flex align-items-center">
            <FontAwesomeIcon icon={faDownload} className="me-2 text-success" style={{ transform: 'rotate(180deg)' }} />
            Import Agents from CSV
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>Instructions:</strong>
            <ol className="mb-0 mt-2">
              <li>Export your current agents to get the CSV template</li>
              <li>Edit the CSV file with your changes (you can edit commission splits, fees, plans, etc.)</li>
              <li>Upload the edited CSV file to update your agents</li>
            </ol>
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>Select CSV File</Form.Label>
            <Form.Control
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
            />
            <Form.Text className="text-muted">
              Upload a CSV file with agent data to update
            </Form.Text>
          </Form.Group>

          {importPreview.length > 0 && (
            <>
              <h6 className="mb-3">Preview ({importPreview.length} agents)</h6>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Plan</th>
                      <th>Monthly Fee</th>
                      <th>Commission Split</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.slice(0, 10).map((row, index) => (
                      <tr key={index}>
                        <td>{row.Name}</td>
                        <td>{row.Email}</td>
                        <td>{row.Plan}</td>
                        <td>${row['Monthly Fee']}</td>
                        <td>{row['Commission Split Agent']}/{row['Commission Split Broker']}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {importPreview.length > 10 && (
                  <small className="text-muted">
                    Showing first 10 of {importPreview.length} agents...
                  </small>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowImportModal(false);
              setImportFile(null);
              setImportPreview([]);
            }}
            disabled={importLoading}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleImportCSV}
            disabled={!importPreview.length || importLoading}
          >
            {importLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Importing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faDownload} className="me-1" style={{ transform: 'rotate(180deg)' }} />
                Import {importPreview.length} Agents
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modern Admin Confirmation Modal */}
      <Modal 
        show={showAdminConfirmModal} 
        onHide={() => {
          setShowAdminConfirmModal(false);
          setAdminConfirmAction(null);
        }}
        centered
        backdrop="static"
        style={{ zIndex: 9999 }}
        dialogClassName="admin-confirm-modal"
      >
        <Modal.Header 
          closeButton
          style={{ 
            background: adminConfirmAction?.isGranting 
              ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' 
              : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            border: 'none'
          }}
        >
          <Modal.Title className="d-flex align-items-center">
            <FontAwesomeIcon 
              icon={faCrown} 
              className="me-2" 
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}
            />
            {adminConfirmAction?.isGranting ? 'Grant Admin Privileges' : 'Remove Admin Privileges'}
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className="py-4" style={{ position: 'relative', zIndex: 10000 }}>
          {adminConfirmAction && (
            <>
              <div className="text-center mb-4">
                <div 
                  className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                  style={{
                    width: '80px',
                    height: '80px',
                    background: adminConfirmAction.isGranting 
                      ? 'rgba(251, 191, 36, 0.1)' 
                      : 'rgba(239, 68, 68, 0.1)',
                    color: adminConfirmAction.isGranting ? '#f59e0b' : '#dc2626'
                  }}
                >
                  <FontAwesomeIcon 
                    icon={adminConfirmAction.isGranting ? faCrown : faTimesCircle} 
                    size="2x"
                  />
                </div>
              </div>
              
              <h5 className="text-center mb-3">
                {adminConfirmAction.isGranting 
                  ? `Grant admin privileges to ${adminConfirmAction.agent.name}?`
                  : `Remove admin privileges from ${adminConfirmAction.agent.name}?`
                }
              </h5>
              
              <Alert 
                variant={adminConfirmAction.isGranting ? 'warning' : 'danger'} 
                className="mb-0"
              >
                <FontAwesomeIcon 
                  icon={adminConfirmAction.isGranting ? faShield : faTimesCircle} 
                  className="me-2"
                />
                {adminConfirmAction.isGranting 
                  ? 'This user will have full administrative access to manage team members, view billing information, and access all broker features.'
                  : 'This user will lose all administrative privileges and will only have regular agent access.'}
              </Alert>
            </>
          )}
        </Modal.Body>
        
        <Modal.Footer style={{ borderTop: '1px solid #e5e7eb' }}>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowAdminConfirmModal(false);
              setAdminConfirmAction(null);
            }}
            disabled={isUpdatingAdmin}
          >
            Cancel
          </Button>
          
          <Button 
            variant={adminConfirmAction?.isGranting ? 'warning' : 'danger'}
            onClick={async () => {
              if (!adminConfirmAction) return;
              
              setIsUpdatingAdmin(true);
              
              try {
                const apiUrl = process.env.NODE_ENV === 'production' ? '/api/broker' : 'http://localhost:8080/api/broker';
                const response = await fetch(`${apiUrl}/agents/${adminConfirmAction.agent.id}/admin`, {
                  method: 'PUT',
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    brokerageId: brokerageId || localStorage.getItem('brokerageId'),
                    isAdmin: adminConfirmAction.isGranting
                  })
                });

                if (response.ok) {
                  // Update local state
                  setSelectedAgent({ ...adminConfirmAction.agent, isAdmin: adminConfirmAction.isGranting });
                  // Refresh the agents list
                  await fetchAgents();
                  
                  // Close modals
                  setShowAdminConfirmModal(false);
                  setAdminConfirmAction(null);
                  
                  // Show success toast or alert (you can replace with a toast notification)
                  setTimeout(() => {
                    alert(`✅ Admin privileges ${adminConfirmAction.isGranting ? 'granted' : 'removed'} successfully!`);
                  }, 300);
                } else {
                  const errorData = await response.json();
                  throw new Error(errorData.message || 'Failed to update admin privileges');
                }
              } catch (error) {
                console.error('Error updating admin privileges:', error);
                alert(`❌ Failed to update admin privileges: ${error instanceof Error ? error.message : 'Unknown error'}`);
              } finally {
                setIsUpdatingAdmin(false);
              }
            }}
            disabled={isUpdatingAdmin}
            className="d-flex align-items-center"
          >
            {isUpdatingAdmin ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Processing...
              </>
            ) : (
              <>
                <FontAwesomeIcon 
                  icon={adminConfirmAction?.isGranting ? faCrown : faTimesCircle} 
                  className="me-2"
                />
                {adminConfirmAction?.isGranting ? 'Grant Admin Access' : 'Remove Admin Access'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Custom Invitation Confirmation Modal */}
      <Modal 
        show={showInvitationModal} 
        onHide={() => {
          setShowInvitationModal(false);
          setAgentToInvite(null);
        }}
        centered
        backdrop="static"
        style={{ zIndex: 9999 }}
        dialogClassName="invitation-confirm-modal"
      >
        <Modal.Header 
          closeButton
          style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none'
          }}
        >
          <Modal.Title className="d-flex align-items-center">
            <FontAwesomeIcon 
              icon={faEnvelope} 
              className="me-2" 
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}
            />
            Send Invitation Email
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className="py-4">
          {agentToInvite && (
            <>
              <div className="text-center mb-4">
                <div 
                  className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                  style={{
                    width: '80px',
                    height: '80px',
                    background: 'rgba(102, 126, 234, 0.1)',
                    color: '#667eea'
                  }}
                >
                  <FontAwesomeIcon icon={faEnvelope} size="2x" />
                </div>
              </div>
              
              <h5 className="text-center mb-4">
                Send invitation email to {agentToInvite.name}?
              </h5>
              
              <Card className="border-0" style={{ background: 'rgba(102, 126, 234, 0.05)' }}>
                <Card.Body>
                  <div className="mb-2">
                    <FontAwesomeIcon icon={faUser} className="me-2 text-muted" />
                    <strong>Name:</strong> {agentToInvite.name}
                  </div>
                  <div className="mb-2">
                    <FontAwesomeIcon icon={faEnvelope} className="me-2 text-muted" />
                    <strong>Email:</strong> {agentToInvite.email}
                  </div>
                  <div className="mb-2">
                    <FontAwesomeIcon icon={faIdCard} className="me-2 text-muted" />
                    <strong>License:</strong> {agentToInvite.licenseNumber}
                  </div>
                  <div className="mb-2">
                    <FontAwesomeIcon icon={faBuilding} className="me-2 text-muted" />
                    <strong>Status:</strong> {getMemberStatusBadge(agentToInvite.memberStatus)}
                  </div>
                </Card.Body>
              </Card>
              
              <Alert variant="info" className="mt-3 mb-0">
                <FontAwesomeIcon icon={faShield} className="me-2" />
                An invitation email will be sent to {agentToInvite.email} with instructions to join your brokerage on Agent Kairos™.
              </Alert>
            </>
          )}
        </Modal.Body>
        
        <Modal.Footer style={{ borderTop: '1px solid #e5e7eb' }}>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowInvitationModal(false);
              setAgentToInvite(null);
            }}
            disabled={isSendingInvitation}
          >
            Cancel
          </Button>
          
          <Button 
            variant="primary"
            onClick={confirmSendInvitation}
            disabled={isSendingInvitation}
            className="d-flex align-items-center"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none'
            }}
          >
            {isSendingInvitation ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Sending...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                Send Invitation
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AgentDirectory;

// Add custom styles for the admin confirmation modal and table scrolling
const style = document.createElement('style');
style.textContent = `
  .admin-confirm-modal .modal-dialog,
  .invitation-confirm-modal .modal-dialog {
    z-index: 9999 !important;
  }
  
  .admin-confirm-modal + .modal-backdrop,
  .invitation-confirm-modal + .modal-backdrop {
    z-index: 9998 !important;
    background-color: rgba(0, 0, 0, 0.7) !important;
  }
  
  .modal-backdrop.show {
    opacity: 0.5;
  }
  
  .admin-confirm-modal .modal-content,
  .invitation-confirm-modal .modal-content {
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3) !important;
    border: none;
    overflow: hidden;
  }
  
  .admin-confirm-modal .modal-header,
  .invitation-confirm-modal .modal-header {
    border-radius: 0;
  }
  
  .admin-confirm-modal.show .modal-dialog,
  .invitation-confirm-modal.show .modal-dialog {
    animation: modalSlideIn 0.3s ease-out;
  }
  
  @keyframes modalSlideIn {
    from {
      transform: translateY(-50px) scale(0.9);
      opacity: 0;
    }
    to {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }
  
  /* Enhanced scrollbar styling */
  .table-responsive::-webkit-scrollbar {
    height: 10px;
  }
  
  .table-responsive::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  .table-responsive::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }
  
  .table-responsive::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
  
  /* Add scroll indicator shadow */
  .table-responsive {
    position: relative;
    box-shadow: 
      inset -20px 0 20px -20px rgba(0,0,0,0.2),
      inset 20px 0 20px -20px rgba(0,0,0,0.2);
  }
  
  /* Sticky first column for better UX */
  .agent-directory table tbody tr td:first-child,
  .agent-directory table thead tr th:first-child {
    position: sticky;
    left: 0;
    z-index: 1;
    background: white;
  }
  
  .agent-directory table thead tr th:first-child {
    z-index: 2;
  }
`;

// Only add the style once
if (!document.getElementById('admin-confirm-modal-styles')) {
  style.id = 'admin-confirm-modal-styles';
  document.head.appendChild(style);
}